/**
 * Domain-agnostic gallery state controller. Each surface that owns a
 * gallery — addon, addon-preset, admission, admission-preset — wraps this
 * hook with its own raw mutations and (optionally) renames the
 * `entityId` argument. The hook itself knows nothing about addons or
 * BE endpoints; it only manages local staging and orchestrates flush
 * phases through the mutation callbacks the caller injects.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Common shape every domain serves for a persisted image entry. */
export interface GalleryImage {
  id: string;
  url: string;
  orderPosition: number;
}

/**
 * Outcome of `flushAllChanges`: the consumer inspects this to decide
 * which toast to surface (success vs partial-image-error). Errors are
 * intentionally swallowed inside the flush so a mid-batch failure doesn't
 * abort the rest of the queue — the caller decides UX.
 */
export interface FlushAllChangesResult {
  /** Number of DELETE mutations that rejected. */
  failedDeletes: number;
  /** Number of POST upload mutations that rejected. */
  failedUploads: number;
  /** Whether the final PUT reorder rejected (or was skipped because there were no images left to send). */
  reorderFailed: boolean;
  /** True if any of the three above is non-zero — handy shorthand for the toast switch. */
  hasErrors: boolean;
}

/**
 * One entry in the bulk-replace manifest the FE ships to the BE in a
 * single multipart `POST .../gallery:replace`. Position in the final
 * gallery is the entry's index in the manifest array + 1.
 *
 * Three kinds, mirroring the BE discriminated union (B2B-1705):
 *
 *   - `keep`   — retain an existing server image on the entity. The
 *                `imageId` must already belong to the entity.
 *   - `clone`  — server-side clone of an image owned by the entity's
 *                linked preset (admission-from-preset only — entity
 *                must carry an `offerPresetId`). BE shares the S3
 *                URL with a fresh image id.
 *   - `upload` — fresh upload. `fileKey` names the multipart binary
 *                part that carries the file's bytes.
 */
export type GalleryManifestEntry =
  | { kind: 'keep'; imageId: string }
  | { kind: 'clone'; presetImageId: string }
  | { kind: 'upload'; fileKey: string };

/**
 * The payload `useGallerySave` ships to the BE on a bulk-flush save.
 * `manifest` is the ordered list of entries; `files` maps every
 * `upload.fileKey` to the actual `File` blob so the orchestrator can
 * append it as a binary multipart part with the same name.
 */
export interface BuildManifestResult {
  manifest: GalleryManifestEntry[];
  files: Map<string, File>;
}

export interface GalleryImagesController {
  /** Mixed list (server + locally-staged + preset-origin) shown in the gallery, in display order. */
  images: GalleryImage[];
  /** Append a new file to the staging area. */
  upload: (file: File) => void;
  /**
   * Remove an image from the staging area. Server images move to `pendingDeletes`
   * (queued for `DELETE` on flush); locally-staged and preset-origin ones are
   * dropped outright (preset items have no server-side admission id to delete).
   */
  delete: (imageId: string) => void;
  /** Rewrite the visible order of `images`. */
  reorder: (orderedIds: string[]) => void;
  /**
   * **Legacy 3-phase flush** — captures the staging area synchronously and
   * runs DELETE → POST → PUT-reorder against the BE. Each phase swallows
   * its own rejections so one failed mutation doesn't abort the batch;
   * failure counts are returned so the caller can show an aggregated
   * toast.
   *
   * Replaced by the manifest-based `buildManifest()` + `setGallery`
   * callback on the orchestrator (B2B-1705 BE bulk endpoint). Stays for
   * surfaces that haven't migrated yet (preset / addon / addon-preset)
   * until their respective tickets land.
   *
   * The executor does **not** write back to the live state — the
   * caller is expected to call `reset()` once the chain settles.
   */
  prepareFlush: () => (entityId: string) => Promise<FlushAllChangesResult>;
  /**
   * Convenience wrapper that captures and executes in one call. Equivalent
   * to `prepareFlush()(entityId)` and kept for tests/consumers that don't
   * need the synchronous-capture guarantee.
   */
  flushAllChanges: (entityId: string) => Promise<FlushAllChangesResult>;
  /**
   * **New bulk-flush builder** — runs synchronously and returns the
   * manifest + binary files map the orchestrator will ship to the BE in
   * a single `POST .../gallery:replace` multipart request. Pure function
   * over the current `display` snapshot: walks it in order, maps each
   * `kind` to the matching manifest entry, packs `File`s under their
   * `fileKey`s.
   *
   * Used by adapters wired against the new BE bulk endpoint
   * (admission via B2B-1706, others to follow). Does not touch state —
   * the caller `reset()`s once the save settles.
   */
  buildManifest: () => BuildManifestResult;
  /**
   * Imperatively re-seed the controller from a fresh list of
   * preset-origin images. Used by the in-modal preset picker
   * (`<SelectOfferFromPresets>`) flow where the user picks a preset
   * after the modal is already open — the picker can't change
   * `initialImages` (the controller is owned by the parent), so it
   * calls `seedImages()` to overwrite the display state.
   *
   * Mirrors RHF's `reset(values)` pattern: revokes any currently-staged
   * blob URLs, drops `pendingDeletes` / `initialServerIds`, and rebuilds
   * `display` from the provided array (all entries become
   * `kind: 'preset'`). The caller passes the preset's persisted
   * `imageId` as `presetImageId` so the next `buildManifest()` can
   * emit `clone` entries.
   *
   * Non-sticky w.r.t. `reset()`: a subsequent `reset()` call rebuilds
   * from the parent's `initialImages` prop (via `initialImagesRef`),
   * NOT from the seeded list. This asymmetry is intentional and
   * load-bearing — the Ruta B cancel→reopen leak fix in
   * `offers-admissions-tab` (PR #607) relies on it to wipe seeded
   * state by calling `reset()` against an empty `initialImages`.
   */
  seedImages: (images: { presetImageId: string; url: string }[]) => void;
  /**
   * Drops every locally-staged change and rebuilds state from the latest
   * `initialImages` snapshot. The caller invokes this after a save
   * settles so the gallery starts the next modal session clean.
   */
  reset: () => void;
  /**
   * True if the current display state differs from the initial server
   * snapshot — any of: pending deletes, staged uploads, preset-origin
   * items, or a reordered display. Used by the orchestrator to skip
   * the bulk POST entirely on edits where the user didn't touch the
   * gallery. Mirrors the same `hasWork` heuristic the legacy
   * `prepareFlush` uses internally — exposed publicly here because
   * the new bulk path needs to decide before building the manifest.
   */
  hasChanges: () => boolean;
  /** True while a flush is in flight. The caller uses it to keep the entity card pulsed. */
  isFlushPending: boolean;
}

/**
 * Async mutation callbacks the caller injects. Each domain provides its
 * own `mutateAsync` for the underlying BE endpoint — this keeps the
 * gallery hook free of any direct dependency on the addon / preset /
 * admission HTTP layer.
 */
export interface GalleryImageMutations {
  uploadImage: (args: { entityId: string; file: File }) => Promise<GalleryImage>;
  deleteImage: (args: { entityId: string; imageId: string }) => Promise<unknown>;
  reorderImages: (args: { entityId: string; imageIds: string[] }) => Promise<unknown>;
}

interface UseGalleryImagesArgs extends GalleryImageMutations {
  /**
   * Identity of the entity currently being edited. Pass the entity id in
   * edit mode and `null`/`undefined` in create mode. The staging area is
   * re-snapshotted from `initialImages` whenever this value changes.
   */
  entityId?: string | null;
  /**
   * Server-side images to seed the gallery with. Re-read whenever
   * `entityId` flips — later changes within the same `entityId` are
   * intentionally ignored to keep in-flight stages stable.
   */
  initialImages?: GalleryImage[];
}

interface ImagesState {
  /** Display list shown in the gallery: server images + staged in user order. */
  display: DisplayItem[];
  /** Server-side image ids the user has marked for deletion. */
  pendingDeletes: string[];
  /** Snapshot of the initial server order — used to detect if a final reorder is needed. */
  initialServerIds: string[];
}

/**
 * Discriminated union so we can tell at flush time whether an entry has a
 * `File` to upload, already exists on the entity's server, or is a
 * preset-origin image waiting to be cloned server-side.
 *
 *   - `server` — persisted on this entity. `id` is the BE image id.
 *   - `staged` — local upload not yet sent. `id` is a synthetic
 *                client-side id used for reorder/delete identity.
 *   - `preset` — comes from the entity's linked preset (admission-from-
 *                preset only). `id` is synthetic; `presetImageId` is the
 *                preset's image id the BE will clone from.
 */
type DisplayItem =
  | { kind: 'server'; id: string; url: string }
  | { kind: 'staged'; id: string; url: string; file: File }
  | { kind: 'preset'; id: string; url: string; presetImageId: string };

/**
 * Generates a deterministic-enough id for a staged file. UUIDs would be
 * overkill — the only requirement is uniqueness within the modal's lifetime.
 */
const buildStagedId = (() => {
  let counter = 0;
  return () => `staged-${Date.now()}-${++counter}`;
})();

/**
 * Synthetic ids for preset-origin display items. Same lifetime guarantees
 * as `buildStagedId` — only needs to be unique within the modal session.
 * Kept separate from `buildStagedId` to make the prefix `preset-…` so the
 * id alone hints at the kind.
 */
const buildPresetDisplayId = (() => {
  let counter = 0;
  return () => `preset-${Date.now()}-${++counter}`;
})();

const buildInitialState = (initialImages: GalleryImage[]): ImagesState => {
  const sorted = [...initialImages].sort((a, b) => a.orderPosition - b.orderPosition);
  return {
    display: sorted.map((img): DisplayItem => ({ kind: 'server', id: img.id, url: img.url })),
    pendingDeletes: [],
    initialServerIds: sorted.map((img) => img.id),
  };
};

const toGalleryImage = (item: DisplayItem, index: number): GalleryImage => ({
  id: item.id,
  url: item.url,
  orderPosition: index + 1,
});

export const useGalleryImages = ({
  entityId = null,
  initialImages = [],
  uploadImage,
  deleteImage: deleteImageMutation,
  reorderImages,
}: UseGalleryImagesArgs): GalleryImagesController => {
  // `initialImages` is re-snapshotted only when `entityId` flips. Keeping the
  // latest value in a ref lets the snapshot effect read the freshest list
  // without re-running on every render.
  const initialImagesRef = useRef<GalleryImage[]>(initialImages);
  initialImagesRef.current = initialImages;

  const [state, setState] = useState<ImagesState>(() => buildInitialState(initialImages));
  const [isFlushPending, setIsFlushPending] = useState(false);

  // Keep a ref in sync so `flushAllChanges` (and the entityId-reset effect)
  // can read the latest snapshot without depending on stale closures.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Reset staging when the active entity changes — covers "open modal for
  // entity A → close → open for entity B" where the parent hook instance
  // outlives the modal mount.
  const lastEntityIdRef = useRef<string | null>(entityId);
  useEffect(() => {
    if (lastEntityIdRef.current === entityId) return;
    lastEntityIdRef.current = entityId;
    // Read previous staged urls via the ref so we don't have to depend on
    // `state.display` (which would loop the effect on every state update).
    stateRef.current.display.forEach((item) => {
      if (item.kind === 'staged') URL.revokeObjectURL(item.url);
    });
    setState(buildInitialState(initialImagesRef.current));
  }, [entityId]);

  // Revoke any in-memory blob URLs on unmount so we don't leak when the
  // modal is torn down mid-session.
  useEffect(() => {
    return () => {
      stateRef.current.display.forEach((item) => {
        if (item.kind === 'staged') URL.revokeObjectURL(item.url);
      });
    };
  }, []);

  const upload = useCallback((file: File) => {
    const staged: DisplayItem = {
      kind: 'staged',
      id: buildStagedId(),
      url: URL.createObjectURL(file),
      file,
    };
    setState((current) => ({ ...current, display: [...current.display, staged] }));
  }, []);

  const handleDelete = useCallback((imageId: string) => {
    setState((current) => {
      const target = current.display.find((item) => item.id === imageId);
      if (!target) return current;
      if (target.kind === 'staged') URL.revokeObjectURL(target.url);
      return {
        ...current,
        display: current.display.filter((item) => item.id !== imageId),
        // Only server images need a BE-side DELETE in the legacy flow.
        // Staged and preset items are client-only (preset items become
        // `clone` entries in the manifest — dropping one means simply
        // omitting it from the next manifest), so they drop outright.
        pendingDeletes: target.kind === 'server' ? [...current.pendingDeletes, target.id] : current.pendingDeletes,
      };
    });
  }, []);

  const seedImages = useCallback((images: { presetImageId: string; url: string }[]) => {
    // Revoke any in-memory blob URLs in the current state before we
    // overwrite display — same hygiene as `reset()`.
    stateRef.current.display.forEach((item) => {
      if (item.kind === 'staged') URL.revokeObjectURL(item.url);
    });
    setState({
      display: images.map(
        (img): DisplayItem => ({
          kind: 'preset',
          id: buildPresetDisplayId(),
          url: img.url,
          presetImageId: img.presetImageId,
        })
      ),
      // Preset seeding clears any prior pending state — the picker is a
      // fresh slate.
      pendingDeletes: [],
      initialServerIds: [],
    });
  }, []);

  const hasChanges = useCallback((): boolean => {
    // Reads off the ref so the answer reflects the latest state even
    // when called outside a render cycle (the orchestrator captures
    // this synchronously inside `save()`).
    const snapshot = stateRef.current;
    if (snapshot.pendingDeletes.length > 0) return true;
    if (snapshot.display.length !== snapshot.initialServerIds.length) return true;
    for (let i = 0; i < snapshot.display.length; i += 1) {
      const item = snapshot.display[i];
      if (item.kind !== 'server') return true;
      if (item.id !== snapshot.initialServerIds[i]) return true;
    }
    return false;
  }, []);

  const buildManifest = useCallback((): BuildManifestResult => {
    // Captured snapshot — read off the ref so the manifest reflects the
    // state at the call site even if React renders concurrently.
    const snapshot = stateRef.current;
    const files = new Map<string, File>();
    let uploadCounter = 0;
    const manifest = snapshot.display.map((item): GalleryManifestEntry => {
      if (item.kind === 'server') {
        return { kind: 'keep', imageId: item.id };
      }
      if (item.kind === 'preset') {
        return { kind: 'clone', presetImageId: item.presetImageId };
      }
      // staged → upload. `fileKey` follows the BE convention `file-N`,
      // indexed by appearance order across the manifest (not all
      // display indices, since not every item is an upload).
      const fileKey = `file-${uploadCounter}`;
      uploadCounter += 1;
      files.set(fileKey, item.file);
      return { kind: 'upload', fileKey };
    });
    return { manifest, files };
  }, []);

  const reset = useCallback(() => {
    // Revoke any in-memory blob URLs still in the current state before
    // dropping it, otherwise they'd outlive the component's awareness of
    // them and only be reclaimed at unmount.
    //
    // Subtle render-gap note: `reset()` rebuilds display from
    // `initialImagesRef.current`, which tracks the latest
    // `initialImages` prop the parent passes in. On the bulk-flush
    // path the adapter writes the BE's materialised gallery into
    // TanStack cache (`setQueryData`), but the parent's
    // `initialImages` is usually derived from a SEPARATE query (e.g.
    // `useGetAdmissions` at the page level) — so for one render
    // after `setGallery` succeeds, the cache and the `initialImages`
    // prop can be momentarily out of sync. Reset uses the prop
    // value, not the cache. The orchestrator's `onSaveSettled`
    // invalidation closes the gap on the next refetch. Same
    // behaviour as the legacy flow, just calling it out.
    stateRef.current.display.forEach((item) => {
      if (item.kind === 'staged') URL.revokeObjectURL(item.url);
    });
    setState(buildInitialState(initialImagesRef.current));
  }, []);

  const reorder = useCallback((orderedIds: string[]) => {
    setState((current) => {
      const byId = new Map(current.display.map((item) => [item.id, item]));
      const next = orderedIds.map((id) => byId.get(id)).filter((item): item is DisplayItem => item !== undefined);
      return { ...current, display: next };
    });
  }, []);

  const prepareFlush = useCallback((): ((flushEntityId: string) => Promise<FlushAllChangesResult>) => {
    // Synchronous capture — running this BEFORE any `await` insulates the
    // flush from any state changes the parent may apply between calling
    // save() and the actual flush execution.
    const snapshot = stateRef.current;

    return async (flushEntityId: string): Promise<FlushAllChangesResult> => {
      const hasWork =
        snapshot.pendingDeletes.length > 0 ||
        snapshot.display.some((item) => item.kind === 'staged') ||
        // Pure reorder (no add/delete) still needs a PUT.
        snapshot.display.map((item) => item.id).join('|') !== snapshot.initialServerIds.join('|');

      if (!hasWork) {
        return { failedDeletes: 0, failedUploads: 0, reorderFailed: false, hasErrors: false };
      }

      setIsFlushPending(true);
      let failedDeletes = 0;
      let failedUploads = 0;
      let reorderFailed = false;

      try {
        // Phase 1 — DELETE in series. Each failure is counted; the queue
        // continues so a single bad delete doesn't abort the rest of the
        // batch. The caller turns the count into an aggregated toast.
        await snapshot.pendingDeletes.reduce<Promise<void>>(
          (chain, imageId) =>
            chain.then(() =>
              deleteImageMutation({ entityId: flushEntityId, imageId })
                .then(() => undefined)
                .catch(() => {
                  failedDeletes += 1;
                })
            ),
          Promise.resolve()
        );

        // Phase 2 — POST uploads in series. Sequential is required because
        // BE assigns `orderPosition = max(existing) + 1`; parallel posts
        // race on the same `max` and 4xx with a unique-key conflict.
        const localToServerImage = new Map<string, GalleryImage>();
        await snapshot.display.reduce<Promise<void>>((chain, item) => {
          if (item.kind !== 'staged') return chain;
          return chain.then(() =>
            uploadImage({ entityId: flushEntityId, file: item.file })
              .then((created) => {
                localToServerImage.set(item.id, created);
              })
              .catch(() => {
                failedUploads += 1;
              })
          );
        }, Promise.resolve());

        // Phase 3 — PUT reorder if the user-facing order doesn't match the
        // natural BE order after deletes+uploads.
        const finalIdsForBE = snapshot.display.flatMap((item) => {
          if (item.kind === 'server') {
            return snapshot.pendingDeletes.includes(item.id) ? [] : [item.id];
          }
          const created = localToServerImage.get(item.id);
          return created ? [created.id] : [];
        });

        const naturalOrder = [
          ...snapshot.initialServerIds.filter((id) => !snapshot.pendingDeletes.includes(id)),
          ...snapshot.display.flatMap((item) =>
            item.kind === 'staged' ? [localToServerImage.get(item.id)?.id].filter((id): id is string => !!id) : []
          ),
        ];

        const orderChanged = finalIdsForBE.join('|') !== naturalOrder.join('|');
        if (orderChanged && finalIdsForBE.length > 0) {
          await reorderImages({ entityId: flushEntityId, imageIds: finalIdsForBE })
            .then(() => undefined)
            .catch(() => {
              reorderFailed = true;
            });
        }

        // No post-flush rewrite of the live state on purpose — see the
        // longer explanation on the matching block in the addon-specific
        // wrapper. The caller (`useGallerySave`) calls `reset()` once the
        // chain settles.
      } finally {
        setIsFlushPending(false);
      }

      return {
        failedDeletes,
        failedUploads,
        reorderFailed,
        hasErrors: failedDeletes > 0 || failedUploads > 0 || reorderFailed,
      };
    };
  }, [uploadImage, deleteImageMutation, reorderImages]);

  const flushAllChanges = useCallback(
    (flushEntityId: string): Promise<FlushAllChangesResult> => prepareFlush()(flushEntityId),
    [prepareFlush]
  );

  const renderImages = useMemo<GalleryImage[]>(
    () => state.display.map((item, index) => toGalleryImage(item, index)),
    [state.display]
  );

  return {
    images: renderImages,
    upload,
    delete: handleDelete,
    reorder,
    prepareFlush,
    flushAllChanges,
    buildManifest,
    seedImages,
    reset,
    hasChanges,
    isFlushPending,
  };
};
